import { authService } from '../services/authService';
import { PlatformMenu, PlatformShellContent, usePlatformNavigation } from '../components/platform/PlatformControls';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ResponsiveNavigation } from '../components/ResponsiveNavigation';
import { AlertTriangle, X } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { tenantApi, User, AlertFilters } from '../services/tenantApi';
import { adminApi } from '../services/adminApi';
import { getStoredTheme, applyTheme, onThemeChanged, type Theme } from '../shared/themeUtils';
import { AlertList } from '../components/AlertList';
import { AlertDetail } from '../components/AlertDetail';
import { OnCallWidget } from '../components/OnCallWidget';
import { IngestionHealthWidget } from '../components/IngestionHealthWidget';
import { ProfileSettings } from '../user-dashboard/ProfileSettings';
import { ScheduleCalendar } from '../user-dashboard/ScheduleCalendar';
import { IntegrationsManagement } from './components/IntegrationsManagement';
import { NotificationsView } from './components/NotificationsView';

import { UsersManagement } from './components/UsersManagement';
import { GroupsManagement } from './components/GroupsManagement';
import { RolesManagement } from './components/RolesManagement';
import { SchedulesManagement } from './components/SchedulesManagement';
import { OnCallPresencePanel } from './components/OnCallPresencePanel';
import { EscalationPoliciesManagement } from './components/EscalationPoliciesManagement';
import { NotificationChannelsManagement } from './components/NotificationChannelsManagement';
import { WorkflowsManagement } from './components/WorkflowsManagement';
import { TenantSettingsManagement } from './components/TenantSettingsManagement';
import { TenantSubscriptionsManagement } from './components/TenantSubscriptionsManagement';
import { MobileEnrollmentPanel } from './components/MobileEnrollmentPanel';
import { MobileDevicesManagement } from './components/MobileDevicesManagement';
import { TenantSmsGatewayManagement } from './components/TenantSmsGatewayManagement';
import { SignalRService } from '../services/signalr';
import { useRealtimeRefresh } from '../shared/useRealtimeRefresh';
import { useInboundRealtimeRefresh } from '../shared/useInboundRealtimeRefresh';
import { HeaderNotifications, type HeaderNotificationItem } from '../components/HeaderNotifications';
import { PortalBanner } from '../components/PortalBanner';
import { NotificationDeliveryIncidentBanner } from '../components/NotificationDeliveryIncidentBanner';
import { OnCallOverrideBanner } from '../components/OnCallOverrideBanner';
import { loadPortalBannerConfig, onPortalBannersChanged, resolvePortalBanners, type PortalBannerConfig } from '../shared/portalBannerStore';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import { dateInputValue } from '../shared/dateTimeFormatting';
import { timezoneDateRangeFilters } from '../shared/timezoneDateRange';
import { applyAlertChange, removeAlertById, type AlertMatchCriteria } from '../shared/alertRealtime';
import './TenantPortal.css';

const OnCallOverridesManagement = React.lazy(() => import('./components/OnCallOverridesManagement'));
const PresenceSettingsCard = React.lazy(() => import('./components/PresenceSettingsCard'));

const PREF_KEY_SHOW_MY_SCHEDULES_ONLY = 'nog.showMySchedulesOnly';

const getStoredShowMySchedulesOnly = (): boolean => {
  try {
    const value = localStorage.getItem(PREF_KEY_SHOW_MY_SCHEDULES_ONLY);
    if (value == null) return false;
    return value === '1';
  } catch {
    return false;
  }
};


const PAGE_SIZE = 100;

type AlertTimeRangeValue =
  | 'last_15_mins'
  | 'last_1_hour'
  | 'last_12_hours'
  | 'last_24_hours'
  | 'last_7_days'
  | 'last_30_days'
  | 'custom';

type PortalView = 
  | 'dashboard'
  | 'alerts' 
  | 'notifications'
  | 'schedule' 
  | 'profile' 
  | 'admin-dashboard'
  | 'admin-users' 
  | 'admin-groups' 
  | 'admin-roles' 
  | 'admin-integrations' 
  | 'admin-schedules' 
  | 'admin-oncall-overrides'
  | 'admin-escalation'
  | 'admin-channels'
  | 'admin-workflows'
  | 'admin-settings'
  | 'admin-subscriptions'
  | 'admin-sms-gateways'
  | 'admin-mobile-enrollment'
  | 'admin-mobile-devices';

export const TenantPortal: React.FC = () => {
  const leavePlatform = usePlatformNavigation();
  const { timezone } = useDateTimePreferences();
  useEffect(() => authService.subscribe(() => setUser(authService.getState().user as User | null)), []);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const notificationsPath = `/${tenantSlug}/notifications`;
  const schemaLink = location.pathname.replace(/\/$/, '') === `/${tenantSlug}/integrations`
    && new URLSearchParams(location.search).get('view') === 'schemas';
  const usersLink = location.pathname.replace(/\/$/, '') === `/${tenantSlug}/dashboard`
    && new URLSearchParams(location.search).get('view') === 'users';
  const [activeView, setView] = useState<PortalView>(() => usersLink ? 'admin-users' : schemaLink ? 'admin-integrations' : location.pathname.replace(/\/$/, '') === notificationsPath ? 'notifications' : 'dashboard');
  const setActiveView = (view: PortalView) => {
    setView(view);
    if (view === 'notifications') navigate(notificationsPath);
    else if (location.pathname.replace(/\/$/, '') === notificationsPath) navigate(`/${tenantSlug}/dashboard`);
  };
  useEffect(() => {
    if (schemaLink) setView('admin-integrations');
    else if (usersLink) setView('admin-users');
    else if (location.pathname.replace(/\/$/, '') === notificationsPath) setView('notifications');
    else setView((current) => current === 'notifications' ? 'dashboard' : current);
  }, [location.pathname, notificationsPath, schemaLink, usersLink]);
  const [user, setUser] = useState<User | null>(null);
  const [tenantName, setTenantName] = useState('Tenant');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  const [bannerConfig, setBannerConfig] = useState<PortalBannerConfig>(() => loadPortalBannerConfig());
  const [dismissedBannerIds, setDismissedBannerIds] = useState<string[]>([]);
  const [showMySchedulesOnly, setShowMySchedulesOnly] = useState<boolean>(getStoredShowMySchedulesOnly);
  const normalizeGuid = (v?: string) => (v || '').trim().replace(/[{}]/g, '').toLowerCase();
  const [signalRDebug, setSignalRDebug] = useState<any>(null);
  const signalRDisposersRef = useRef<Array<() => void>>([]);
  const mountedRef = useRef(true);
  const alertRequestRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      signalRDisposersRef.current.splice(0).forEach(dispose => dispose());
    };
  }, []);
  const headerRefreshTimerRef = useRef<number | null>(null);
  const headerRefreshInFlightRef = useRef(false);
  const headerRefreshQueuedRef = useRef(false);
  const [headerNotifications, setHeaderNotifications] = useState<HeaderNotificationItem[]>([]);
  const [notificationsReloadToken, setNotificationsReloadToken] = useState(0);
  const refreshInboundNotifications = useInboundRealtimeRefresh(() => setNotificationsReloadToken(current => current + 1));
  const headerNotificationsRef = useRef<HeaderNotificationItem[]>([]);

  useEffect(() => {
    headerNotificationsRef.current = headerNotifications;
  }, [headerNotifications]);

  const mergeAlert = (existing: any, incoming: any) => {
    const merged = { ...(existing || {}) };
    Object.entries(incoming || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        (merged as any)[key] = value;
      }
    });
    if (!Array.isArray((merged as any).timeline)) {
      (merged as any).timeline = Array.isArray((existing as any)?.timeline)
        ? (existing as any).timeline
        : [];
    }
    return merged;
  };

  const toSafeAlert = (incoming: any) => ({
    ...incoming,
    alertId: (incoming as any)?.alertId || '',
    status: (incoming as any)?.status || 'Open',
    severity: (incoming as any)?.severity || 'Medium',
    priority: (incoming as any)?.priority || 'P3',
    service: (incoming as any)?.service || 'Unknown Service',
    source: (incoming as any)?.source || 'Unknown Source',
    title: (incoming as any)?.title || 'Alert',
    description: (incoming as any)?.description || '',
    createdAt: (incoming as any)?.createdAt || new Date().toISOString(),
    updatedAt: (incoming as any)?.updatedAt || (incoming as any)?.createdAt || new Date().toISOString(),
    timeline: Array.isArray((incoming as any)?.timeline) ? (incoming as any).timeline : [],
  });

  useEffect(() => { applyTheme(theme); }, []);

  useEffect(() => {
    return onThemeChanged((nextTheme) => setTheme(nextTheme));
  }, []);

  useEffect(() => {
    return onPortalBannersChanged((config) => {
      setBannerConfig(config);
      setDismissedBannerIds([]);
    });
  }, []);

  const handleThemeChange = (t: Theme) => { setTheme(t); applyTheme(t); };

  const dismissPortalBanner = useCallback((id: string) => {
    setDismissedBannerIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  // Alert state
  const [alerts, setAlerts] = useState<any[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [bulkActionBusy, setBulkActionBusy] = useState(false);
  const [bulkActionMessage, setBulkActionMessage] = useState<string>('');
  const [closeAllAlertIds, setCloseAllAlertIds] = useState<string[] | null>(null);
  const closeAllSubmitting = useRef(false);
  const closeAllCancelButton = useRef<HTMLButtonElement>(null);
  const closeAllConfirmButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!closeAllAlertIds) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeAllCancelButton.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation();
        if (!closeAllSubmitting.current) setCloseAllAlertIds(null);
      }
      if (event.key === 'Tab') {
        const first = closeAllCancelButton.current;
        const last = closeAllConfirmButton.current;
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey, true);
      previousFocus?.focus();
    };
  }, [closeAllAlertIds]);

  // Filter state
  const [filterStatus, setFilterStatus] = useState<string>('Open');
  const [filterSeverity, setFilterSeverity] = useState<string>('');
  const [filterService, setFilterService] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [filterTimeRange, setFilterTimeRange] = useState<AlertTimeRangeValue>('last_1_hour');
  const [filterCustomStartDate, setFilterCustomStartDate] = useState<string>('');
  const [filterCustomEndDate, setFilterCustomEndDate] = useState<string>('');
  const [services, setServices] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolveTimeWindow = useCallback((): { startUtc?: string; endUtc?: string } => {
    const now = new Date();
    const toIso = (date: Date) => date.toISOString();
    const fromDuration = (durationMs: number) => ({
      startUtc: toIso(new Date(now.getTime() - durationMs)),
      endUtc: toIso(now),
    });

    switch (filterTimeRange) {
      case 'last_15_mins':
        return fromDuration(15 * 60 * 1000);
      case 'last_1_hour':
        return fromDuration(60 * 60 * 1000);
      case 'last_12_hours':
        return fromDuration(12 * 60 * 60 * 1000);
      case 'last_24_hours':
        return fromDuration(24 * 60 * 60 * 1000);
      case 'last_7_days':
        return fromDuration(7 * 24 * 60 * 60 * 1000);
      case 'last_30_days':
        return fromDuration(30 * 24 * 60 * 60 * 1000);
      case 'custom': {
        return timezoneDateRangeFilters(filterCustomStartDate, filterCustomEndDate, timezone)
          ?? fromDuration(60 * 60 * 1000);
      }
      default:
        return fromDuration(60 * 60 * 1000);
    }
  }, [filterTimeRange, filterCustomStartDate, filterCustomEndDate, timezone]);

  const handleTimeRangeChange = useCallback((value: AlertTimeRangeValue) => {
    setFilterTimeRange(value);
    if (value !== 'custom') {
      return;
    }

    const now = new Date();
    if (!filterCustomStartDate) {
      setFilterCustomStartDate(dateInputValue(now, { timezone }));
    }
    if (!filterCustomEndDate) {
      setFilterCustomEndDate(dateInputValue(now, { timezone }));
    }
  }, [filterCustomStartDate, filterCustomEndDate, timezone]);

  const mapStatusToApiFilter = useCallback((status: string): { status?: string; assignedTo?: string } => {
    switch (status) {
      case 'All':
        return { status: 'All' };
      case 'Open':
        return { status: 'OpenActive' };
      case 'UnAcked':
        return { status: 'UnAcked' };
      case 'Closed':
        return { status: 'Closed' };
      case 'AssignedToMe': {
        const currentUserId = user?.id || user?.userId;
        if (currentUserId) {
          return { status: 'All', assignedTo: currentUserId };
        }
        return { status: 'All' };
      }
      default:
        return { status: status || undefined };
    }
  }, [user?.id, user?.userId]);

  // Build current filters object
  const buildFilters = useCallback((skip = 0): AlertFilters => {
    const mapped = mapStatusToApiFilter(filterStatus);
    const timeWindow = resolveTimeWindow();
    return {
      status: mapped.status,
      assignedTo: mapped.assignedTo,
      severity: filterSeverity || undefined,
      service: filterService || undefined,
      search: filterSearch || undefined,
      startUtc: timeWindow.startUtc,
      endUtc: timeWindow.endUtc,
      limit: PAGE_SIZE,
      skip,
    };
  }, [filterStatus, filterSeverity, filterService, filterSearch, mapStatusToApiFilter, resolveTimeWindow]);

  const loadAllAlertsForCurrentFilters = useCallback(async (): Promise<{ alerts: any[]; totalCount: number }> => {
    let skip = 0;
    let total = 0;
    const combined: any[] = [];

    while (total === 0 || combined.length < total) {
      const page = await tenantApi.getAlertsWithMeta(buildFilters(skip));
      if (skip === 0) {
        total = page.totalCount;
      }

      if (!page.alerts || page.alerts.length === 0) {
        break;
      }

      combined.push(...page.alerts);
      skip += page.alerts.length;

    }

    return { alerts: combined, totalCount: total };
  }, [buildFilters]);

  const loadHeaderNotifications = useCallback(async () => {
    if (headerRefreshInFlightRef.current) {
      headerRefreshQueuedRef.current = true;
      return;
    }

    headerRefreshInFlightRef.current = true;
    try {
      const items = await tenantApi.getNotifications({ take: 80 });
      const mapped: HeaderNotificationItem[] = items.map((item) => {
        const severity = String(item.severity || '').toLowerCase();
        const normalizedSeverity: HeaderNotificationItem['severity'] =
          severity === 'critical'
            ? 'critical'
            : severity === 'high'
              ? 'high'
              : severity === 'medium'
                ? 'medium'
                : severity === 'low'
                  ? 'low'
                  : 'info';

        return {
          id: item.notificationId,
          title: item.title || item.service || (item.alertId ? 'Alert notification' : 'Notification'),
          subtitle: item.subtitle,
          scope: (item.scope || 'shared') as HeaderNotificationItem['scope'],
          severity: normalizedSeverity,
          createdAt: item.updatedAt || item.createdAt,
          isUnread: Boolean(item.isUnread),
          deepLink: item.sourceType === 'schema-remediation' ? item.deepLink : undefined,
          sourceType: item.sourceType,
        };
      });

      setHeaderNotifications(mapped);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      headerRefreshInFlightRef.current = false;
      if (headerRefreshQueuedRef.current) {
        headerRefreshQueuedRef.current = false;
        window.setTimeout(() => {
          loadHeaderNotifications();
        }, 50);
      }
    }
  }, []);

  const queueHeaderNotificationsRefresh = useCallback((delayMs = 150) => {
    if (headerRefreshTimerRef.current) {
      window.clearTimeout(headerRefreshTimerRef.current);
    }

    headerRefreshTimerRef.current = window.setTimeout(() => {
      headerRefreshTimerRef.current = null;
      loadHeaderNotifications();
    }, delayMs);
  }, [loadHeaderNotifications]);

  const markNotificationReadOptimistic = useCallback(async (id: string) => {
    const before = headerNotificationsRef.current.find((x) => x.id === id);
    setHeaderNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, isUnread: false } : item)));

    try {
      await tenantApi.markNotificationRead(id);
      queueHeaderNotificationsRefresh();
    } catch (err) {
      if (before?.isUnread) {
        setHeaderNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, isUnread: true } : item)));
      }
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  }, [queueHeaderNotificationsRefresh]);

  const clearNotificationOptimistic = useCallback(async (id: string) => {
    const removed = headerNotificationsRef.current.find((x) => x.id === id);
    setHeaderNotifications((prev) => prev.filter((item) => item.id !== id));

    try {
      await tenantApi.clearNotification(id, removed?.scope);
      queueHeaderNotificationsRefresh();
    } catch (err) {
      if (removed) {
        setHeaderNotifications((prev) => {
          if (prev.some((item) => item.id === removed.id)) {
            return prev;
          }
          return [removed, ...prev];
        });
      }
      console.error('Failed to clear notification:', err);
      throw err;
    }
  }, [queueHeaderNotificationsRefresh]);

  const clearVisibleNotificationsOptimistic = useCallback(async (scope: 'all' | HeaderNotificationItem['scope']) => {
    const removed = headerNotificationsRef.current.filter((item) => scope === 'all' || item.scope === scope);
    setHeaderNotifications((prev) => prev.filter((item) => !(scope === 'all' || item.scope === scope)));

    try {
      await tenantApi.clearNotifications({ scope });
      queueHeaderNotificationsRefresh();
    } catch (err) {
      if (removed.length > 0) {
        setHeaderNotifications((prev) => {
          const existing = new Set(prev.map((item) => item.id));
          const toRestore = removed.filter((item) => !existing.has(item.id));
          return [...toRestore, ...prev];
        });
      }
      console.error('Failed to clear visible notifications:', err);
      throw err;
    }
  }, [queueHeaderNotificationsRefresh]);

  // Load alerts (replaces list, e.g. on filter change)
  const loadAlerts = useCallback(async () => {
    const request = ++alertRequestRef.current;
    try {
      setAlertsLoading(true);
      const result = await loadAllAlertsForCurrentFilters();
      if (request !== alertRequestRef.current || !mountedRef.current) return;
      setAlerts(result.alerts);
      setTotalCount(result.totalCount);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      if (request === alertRequestRef.current) setAlertsLoading(false);
    }
  }, [loadAllAlertsForCurrentFilters]);

  // Load more (append to existing list)
  const loadMoreAlerts = loadAlerts;

  const hasMore = false;

  // Reload alerts when filters change (except search, which is debounced)
  useEffect(() => {
    if (!loading) void loadAlerts();
  }, [loading, filterStatus, filterSeverity, filterService, filterTimeRange, filterCustomStartDate, filterCustomEndDate, timezone, user?.id, user?.userId]);

  // Debounced search
  useEffect(() => {
    if (loading) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadAlerts();
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [filterSearch]);

  const loadAlertsRef = useRef(loadAlerts);
  loadAlertsRef.current = loadAlerts;
  const alertCriteriaRef = useRef<() => AlertMatchCriteria>(() => ({}));
  alertCriteriaRef.current = () => {
    const current = buildFilters();
    return {
      status: current.status,
      assignedTo: current.assignedTo,
      severity: current.severity,
      service: current.service,
      search: current.search,
      startUtc: current.startUtc,
      // Relative ranges end at "now", so only custom ranges have a real upper bound.
      endUtc: filterTimeRange === 'custom' ? current.endUtc : undefined,
    };
  };
  const countRefreshTimerRef = useRef<number | null>(null);
  const refreshCountsRef = useRef<() => void>(() => undefined);
  const scheduleCountRefresh = useCallback(() => {
    if (countRefreshTimerRef.current !== null) window.clearTimeout(countRefreshTimerRef.current);
    countRefreshTimerRef.current = window.setTimeout(() => {
      countRefreshTimerRef.current = null;
      refreshCountsRef.current();
    }, 750);
  }, []);
  useEffect(() => () => {
    if (countRefreshTimerRef.current !== null) window.clearTimeout(countRefreshTimerRef.current);
  }, []);
  useRealtimeRefresh(() => {
    if (loading) return;
    void loadAlerts();
    scheduleCountRefresh();
    void loadHeaderNotifications();
    setNotificationsReloadToken(current => current + 1);
    const selectedId = selectedAlert?.alertId;
    if (selectedId) {
      void tenantApi.getAlert(selectedId)
        .then((updated) => {
          if (!updated) return;
          setSelectedAlert((prev: any) => {
            if (normalizeGuid(prev?.alertId) !== normalizeGuid(selectedId)) return prev;
            return mergeAlert(prev, updated);
          });
        })
        .catch(err => console.error('Failed to refresh selected alert:', err));
    }
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadHeaderNotifications();
    return () => {
      if (headerRefreshTimerRef.current) {
        window.clearTimeout(headerRefreshTimerRef.current);
      }
    };
  }, [loadHeaderNotifications]);

  useEffect(() => {
    const onSettingsChanged = (evt: any) => {
      if (typeof evt?.detail?.showMySchedulesOnly === 'boolean') {
        setShowMySchedulesOnly(evt.detail.showMySchedulesOnly);
      }
    };

    const onStorage = (evt: StorageEvent) => {
      if (evt.key === PREF_KEY_SHOW_MY_SCHEDULES_ONLY) {
        setShowMySchedulesOnly(getStoredShowMySchedulesOnly());
      }
    };

    window.addEventListener('nog:settings:changed', onSettingsChanged as EventListener);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('nog:settings:changed', onSettingsChanged as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const loadInitialData = async () => {
    try {
      const currentUser = await tenantApi.getCurrentUser();
      setUser(currentUser);
      
      const tenantInfo = await adminApi.getTenantInfo();
      if (tenantInfo?.name) setTenantName(tenantInfo.name);
      
      // Load alerts
      await loadAlertsRef.current();

      // Load services for filter dropdown
      try {
        const svc = await tenantApi.getAlertServices();
        setServices(svc);
      } catch { /* non-critical */ }

      // Load users for assign dropdown
      try {
        const userData = await tenantApi.getUsers({ take: 200 });
        setUsers(userData.users || []);
      } catch { /* non-critical */ }

      // Connect SignalR with actual tenantId/userId
      initializeSignalR(tenantInfo?.tenantId, currentUser?.id || currentUser?.userId);
    } catch (err) {
      console.error('Failed to load initial portal data:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const initializeSignalR = async (tid?: string, uid?: string) => {
    try {
      const signalR = SignalRService.getInstance();
      if (!mountedRef.current || signalRDisposersRef.current.length) return;

      // Patch the fully-loaded list in place; a full reload on every event caused visible churn.
      const patchAlerts = (update: (prev: any[]) => any[]) => {
        setAlerts((prev) => {
          const next = update(prev);
          if (next !== prev) setTotalCount(next.length);
          return next;
        });
      };
      signalRDisposersRef.current.push(signalR.onAlertCreated((alert) => {
        const a = { ...toSafeAlert(alert), id: (alert as any)?.alertId };
        patchAlerts((prev) => applyAlertChange(prev, a, alertCriteriaRef.current(), mergeAlert));
        scheduleCountRefresh();
        queueHeaderNotificationsRefresh();
      }));
      signalRDisposersRef.current.push(signalR.onAlertUpdated((alert) => {
        const a = { ...alert, id: alert.alertId };
        const incomingId = normalizeGuid(a.alertId);
        if (!incomingId) {
          console.warn('SignalR AlertUpdated missing alertId', alert);
          return;
        }
        patchAlerts((prev) => applyAlertChange(prev, a, alertCriteriaRef.current(), mergeAlert));
        setSelectedAlert((prev: any) => {
          if (normalizeGuid(prev?.alertId) !== incomingId) return prev;
          return mergeAlert(prev, a);
        });
        scheduleCountRefresh();
        queueHeaderNotificationsRefresh();
      }));
      signalRDisposersRef.current.push(signalR.onAlertDeleted((alertId) => {
        const deletedId = normalizeGuid(alertId);
        patchAlerts((prev) => removeAlertById(prev, alertId));
        setSelectedAlert((prev: any) => normalizeGuid(prev?.alertId) === deletedId ? null : prev);
        scheduleCountRefresh();
        queueHeaderNotificationsRefresh();
      }));

      signalRDisposersRef.current.push(signalR.onNotificationInboxChanged(() => {
        queueHeaderNotificationsRefresh();
      }));
      signalRDisposersRef.current.push(signalR.onInboundNotificationChanged(refreshInboundNotifications));

      // Actually connect if we have tenantId/userId
      if (tid && uid) {
        await signalR.connect(tid, uid);
      }
    } catch (err) {
      console.error('Failed to initialize SignalR:', err);
    }
  };

  useEffect(() => {
    const tenantId = (user as any)?.tenantId;
    const userId = user?.id;
    if (!tenantId || !userId) {
      return;
    }

    const signalR = SignalRService.getInstance();
    const ensure = () => {
      signalR.ensureConnected(tenantId, userId).catch((err: unknown) => {
        console.error('SignalR keepalive ensureConnected failed:', err);
      });
    };

    const interval = window.setInterval(ensure, 10000);
    const onFocus = () => ensure();
    const onOnline = () => ensure();

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [(user as any)?.tenantId, user?.id]);

  useEffect(() => {
    const onDebug = (evt: Event) => {
      const detail = (evt as CustomEvent).detail;
      setSignalRDebug(detail);
    };
    window.addEventListener('nog:signalr:debug', onDebug as EventListener);
    return () => {
      window.removeEventListener('nog:signalr:debug', onDebug as EventListener);
    };
  }, []);

  // Action handlers that refresh the list
  const handleAcknowledge = async (alertId: string) => {
    try {
      await tenantApi.acknowledgeAlert(alertId);
      await loadAlerts();
      if (selectedAlert?.alertId === alertId) {
        const updated = await tenantApi.getAlert(alertId);
        setSelectedAlert(updated);
      }
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const handleCloseAlert = async (alertId: string, resolutionNote?: string) => {
    try {
      await tenantApi.closeAlert(alertId, resolutionNote);
      await loadAlerts();
      if (selectedAlert?.alertId === alertId) {
        const updated = await tenantApi.getAlert(alertId);
        setSelectedAlert(updated);
      }
    } catch (err) {
      console.error('Failed to close alert:', err);
    }
  };

  const handleAssign = async (alertId: string, userId: string) => {
    try {
      await tenantApi.assignAlert(alertId, userId);
      await loadAlerts();
      if (selectedAlert?.alertId === alertId) {
        const updated = await tenantApi.getAlert(alertId);
        setSelectedAlert(updated);
      }
    } catch (err) {
      console.error('Failed to assign alert:', err);
    }
  };

  const handleChangePriority = async (alertId: string, priority: string) => {
    try {
      await tenantApi.changePriority(alertId, priority);
      const updated = await tenantApi.getAlert(alertId);
      setSelectedAlert(updated);
      await loadAlerts();
    } catch (err) {
      console.error('Failed to change priority:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await tenantApi.logout();
    } finally {
      window.location.href = '/';
    }
  };

  const normalizedRoles = new Set((user?.roles || []).map((r) => String(r || '').toLowerCase()));
  const normalizedPermissions = new Set((user?.permissions || []).map((p) => String(p || '').toLowerCase()));
  const isTenantAdmin = normalizedRoles.has('tenantadmin') || normalizedRoles.has('tenant admin');
  const isAdmin = normalizedRoles.has('tenantadmin') || normalizedRoles.has('tenant admin') || normalizedPermissions.has('manageusers');
  const isTeamLead = normalizedRoles.has('teamlead') || normalizedRoles.has('team lead');
  const canManageOverrides = isTenantAdmin || normalizedRoles.has('platformadmin') || normalizedRoles.has('superadmin');
  const canManageMobileEnrollment = isAdmin || isTeamLead;
  const canSeeIngestionWidget = isAdmin || isTeamLead;
  const canAdministerClones = isTenantAdmin || normalizedRoles.has('superadmin');
  const portalBanners = useMemo(
    () =>
      resolvePortalBanners(bannerConfig, {
        tenantSlug,
        tenantId: (user as any)?.tenantId,
        audience: isAdmin ? 'tenant-admin' : 'tenant-user',
      }).filter((item) => !dismissedBannerIds.includes(item.id)),
    [bannerConfig, dismissedBannerIds, isAdmin, tenantSlug, (user as any)?.tenantId],
  );
  const [openAlertsCount, setOpenAlertsCount] = useState(0);
  const [openNewCount, setOpenNewCount] = useState(0);
  const [openAckedCount, setOpenAckedCount] = useState(0);

  const refreshOpenBreakdownCounts = useCallback(async () => {
    try {
      const [openResult, newResult] = await Promise.all([
        tenantApi.getAlertsWithMeta({ status: 'OpenActive', limit: 1, skip: 0 }),
        tenantApi.getAlertsWithMeta({ status: 'UnAcked', limit: 1, skip: 0 }),
      ]);

      const open = openResult.totalCount || 0;
      const openNew = newResult.totalCount || 0;
      const openAcked = Math.max(0, open - openNew);

      setOpenAlertsCount(open);
      setOpenNewCount(openNew);
      setOpenAckedCount(openAcked);
    } catch {
      setOpenAlertsCount(0);
      setOpenNewCount(0);
      setOpenAckedCount(0);
    }
  }, []);

  refreshCountsRef.current = () => { void refreshOpenBreakdownCounts(); };

  useEffect(() => {
    if (loading) return;
    refreshOpenBreakdownCounts();
  }, [loading, refreshOpenBreakdownCounts]);

  const getInitials = (u: User | null): string => {
    if (!u) return '?';
    const name = u.displayName || u.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const renderContent = () => {
    switch (activeView) {
      case 'notifications':
        return <NotificationsView reloadToken={notificationsReloadToken} canViewIncidents={isAdmin || isTeamLead} isTenantAdmin={isTenantAdmin}
          canViewEmailHistory={isTenantAdmin || normalizedRoles.has('platformadmin')}
          canManageIncidents={isTenantAdmin || normalizedRoles.has('platformadmin')}
          onChanged={() => setNotificationsReloadToken(current => current + 1)} />;
      case 'dashboard':
      case 'admin-dashboard':
        return (
          <DashboardView
            isAdmin={isAdmin}
            canSeeIngestionWidget={canSeeIngestionWidget}
            openAlertsCount={openAlertsCount}
            openNewCount={openNewCount}
            openAckedCount={openAckedCount}
            userCount={users.length}
            currentUserId={user?.id || user?.userId}
            showOnlyMine={showMySchedulesOnly}
            onGoAlerts={() => setActiveView('alerts')}
            onGoSchedule={() => setActiveView('schedule')}
          />
        );
      case 'alerts':
        return (
          <div className="alerts-page-shell">
            <header className="alerts-page-header">
              <div>
                <h2>Alerts</h2>
                <p>Live incidents, filtering, triage, and response actions.</p>
              </div>
              <div className="alert-count-group">
                <span className="alert-count-badge">{totalCount} total</span>
                <span className="alert-count-badge">{openAlertsCount} open</span>
                <span className="alert-count-badge">{openNewCount} new</span>
                <span className="alert-count-badge">{openAckedCount} acked</span>
              </div>
            </header>
            <div className="alerts-page-containers">
              <section className="alerts-workspace-container">
                <div className="portal-alerts-layout">
                  <div className="portal-alerts-main">
                    {signalRDebug && (
                      <div style={{ margin: '0 8px 0.5rem', fontSize: '0.75rem', color: '#5c6b7a' }}>
                        Realtime: {signalRDebug.kind} {signalRDebug.eventName ? `(${signalRDebug.eventName})` : ''} {signalRDebug.alertId ? `#${signalRDebug.alertId}` : ''}
                      </div>
                    )}
                    <div className="alerts-filter-bar">
                      <input
                        type="search"
                        placeholder="Search alerts..."
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        className="filter-search"
                      />
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="filter-select"
                      >
                        <option value="All">All</option>
                        <option value="Open">Open (New + Acked)</option>
                        <option value="UnAcked">Un-Acked (New)</option>
                        <option value="Closed">Closed</option>
                        <option value="AssignedToMe">Assigned to me</option>
                      </select>
                      <select
                        value={filterSeverity}
                        onChange={(e) => setFilterSeverity(e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Severity</option>
                        <option value="Critical">Critical</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                      <select
                        value={filterService}
                        onChange={(e) => setFilterService(e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Services</option>
                        {services.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <select
                        value={filterTimeRange}
                        onChange={(e) => handleTimeRangeChange(e.target.value as AlertTimeRangeValue)}
                        className="filter-select"
                        title="Time range"
                      >
                        <option value="last_15_mins">Last 15 mins</option>
                        <option value="last_1_hour">Last 1 Hour</option>
                        <option value="last_12_hours">Last 12 Hours</option>
                        <option value="last_24_hours">Last 24 hours</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="custom">Custom Range</option>
                      </select>
                      {filterTimeRange === 'custom' ? (
                        <div className="filter-custom-range-block">
                          <div className="filter-custom-range-row">
                            <label className="filter-date-label" htmlFor="alerts-custom-start">Start date</label>
                            <input
                              id="alerts-custom-start"
                              type="date"
                              value={filterCustomStartDate}
                              onChange={(e) => setFilterCustomStartDate(e.target.value)}
                              className="filter-date-input"
                              aria-label="Custom start date"
                              max={filterCustomEndDate || undefined}
                            />
                            <label className="filter-date-label" htmlFor="alerts-custom-end">End date</label>
                            <input
                              id="alerts-custom-end"
                              type="date"
                              value={filterCustomEndDate}
                              onChange={(e) => setFilterCustomEndDate(e.target.value)}
                              className="filter-date-input"
                              aria-label="Custom end date"
                              min={filterCustomStartDate || undefined}
                            />
                          </div>
                          <button onClick={loadAlerts} className="btn-sm btn-secondary filter-refresh-btn" title="Refresh">
                            Refresh
                          </button>
                        </div>
                      ) : (
                        <button onClick={loadAlerts} className="btn-sm btn-secondary" title="Refresh">
                          Refresh
                        </button>
                      )}
                    </div>
                    {bulkActionMessage ? (
                      <div className="bulk-action-status">{bulkActionMessage}</div>
                    ) : null}
                    {alertsLoading && alerts.length === 0 ? (
                      <div className="loading">Loading alerts...</div>
                    ) : (
                      <AlertList
                        alerts={alerts}
                        selectedAlertId={selectedAlert?.alertId}
                        onSelect={setSelectedAlert}
                        onAcknowledge={(alertId) => handleAcknowledge(alertId)}
                        onClose={(alertId) => handleCloseAlert(alertId)}
                        hasMore={hasMore}
                        onLoadMore={loadMoreAlerts}
                        loadingMore={alertsLoading}
                      />
                    )}
                    {isAdmin && (
                      <div className="alerts-bulk-actions-bar">
                        <button
                          onClick={() => void handleAcknowledgeAllNew()}
                          className="btn-sm btn-secondary"
                          disabled={bulkActionBusy || alertsLoading}
                          title="Acknowledge all new alerts in current list"
                        >
                          Ack All New
                        </button>
                        <button
                          onClick={() => void handleCloseAllOpen()}
                          className="btn-sm btn-secondary"
                          disabled={bulkActionBusy || alertsLoading}
                          title="Close all open alerts in current list"
                        >
                          Close All Open
                        </button>
                      </div>
                    )}
                  </div>
                  {selectedAlert && (
                    <AlertDetail
                      alert={selectedAlert}
                      loadWorkflowProgress={tenantApi.getAlertWorkflowProgress}
                      subscribeWorkflowProgress={callback => SignalRService.getInstance().onWorkflowProgress(callback)}
                      onAcknowledge={() => handleAcknowledge(selectedAlert.alertId)}
                      onAddNote={async (note) => {
                        await tenantApi.addNote(selectedAlert.alertId, note);
                        const updated = await tenantApi.getAlert(selectedAlert.alertId);
                        setSelectedAlert(updated);
                      }}
                      onClose={(note) => handleCloseAlert(selectedAlert.alertId, note)}
                      onReopen={async () => {
                        const alertId = selectedAlert.alertId;
                        await tenantApi.reopenAlert(alertId);
                        setSelectedAlert(await tenantApi.getAlert(alertId));
                        await loadAlerts();
                      }}
                      onClosePanel={() => setSelectedAlert(null)}
                      onChangePriority={(priority) => handleChangePriority(selectedAlert.alertId, priority)}
                      onAssign={(userId) => handleAssign(selectedAlert.alertId, userId)}
                      users={users}
                    />
                  )}
                </div>
              </section>

              <aside className="alerts-general-info-container portal-sidebar-right">
                <OnCallWidget
                  currentUserId={user?.id || user?.userId}
                  showOnlyMine={showMySchedulesOnly}
                  showPresence={isAdmin || isTeamLead}
                  onViewSchedule={() => setActiveView('schedule')}
                />
                <IngestionHealthWidget compact={true} />
              </aside>
            </div>
          </div>
        );
      case 'schedule':
        return (
          <>
              <React.Suspense fallback={null}>
                <OnCallPresencePanel />
              </React.Suspense>
            <ScheduleCalendar
              currentUserId={user?.id || user?.userId}
              showOnlyMine={showMySchedulesOnly}
            />
          </>
        );
      case 'profile':
        return (
          <ProfileSettings 
            user={user} 
            onUpdate={async (data) => {
              await tenantApi.updateProfile(data);
              setUser(await tenantApi.getCurrentUser());
            }}
            onOpenMobileEnrollment={() => setActiveView('admin-mobile-enrollment')}
          />
        );
      
      // Admin Views
      case 'admin-users':
        return (
          <div className="admin-split-view">
            <UsersManagement />
            <GroupsManagement />
          </div>
        );
      case 'admin-roles':
        return <RolesManagement />;
      case 'admin-integrations':
        return <IntegrationsManagement isTenantAdmin={isTenantAdmin}
          canCloneInbound={canAdministerClones || normalizedPermissions.has('manageintegrations') || normalizedPermissions.has('integrations:write')}
          canCloneOutbound={canAdministerClones || normalizedPermissions.has('manageoutboundintegrations')} />;
      case 'admin-schedules':
        return <SchedulesManagement onOpenOverrides={canManageOverrides ? () => setActiveView('admin-oncall-overrides') : undefined} />;
      case 'admin-oncall-overrides':
        return (
          <React.Suspense fallback={<div className="loading">Loading overrides...</div>}>
            <OnCallOverridesManagement canManage={canManageOverrides} onOpenSchedules={() => setActiveView('admin-schedules')} />
          </React.Suspense>
        );
      case 'admin-escalation':
        return <EscalationPoliciesManagement />;
      case 'admin-channels':
        return <NotificationChannelsManagement />;
      case 'admin-workflows':
        return <WorkflowsManagement canClone={canAdministerClones || normalizedPermissions.has('manageworkflows') ||
          normalizedPermissions.has('workflows:write') || normalizedPermissions.has('rules:write')} />;
      case 'admin-settings':
        return (
          <>
            <TenantSettingsManagement onOpenSubscriptions={() => setActiveView('admin-subscriptions')} />
            <React.Suspense fallback={null}>
              <PresenceSettingsCard canEdit={canManageOverrides} />
            </React.Suspense>
          </>
        );
      case 'admin-subscriptions':
        return <TenantSubscriptionsManagement onBackToSettings={() => setActiveView('admin-settings')} />;
      case 'admin-sms-gateways':
        return <TenantSmsGatewayManagement />;
      case 'admin-mobile-enrollment':
        return <MobileEnrollmentPanel tenantSlug={tenantSlug || 'demo'} tenantName={tenantName} />;
      case 'admin-mobile-devices':
        return <MobileDevicesManagement />;
      // ... placeholder for other admin views migrated from AdminPortal.tsx
      default:
        return <div className="placeholder-view">View {activeView} coming soon...</div>;
    }
  };

  const runBulkAlertAction = async (
    label: string,
    ids: string[],
    action: (alertId: string) => Promise<void>,
  ) => {
    if (ids.length === 0) {
      setBulkActionMessage(`No alerts eligible for ${label.toLowerCase()}.`);
      return;
    }

    setBulkActionBusy(true);
    setBulkActionMessage(`${label}: processing ${ids.length} alerts...`);

    let succeeded = 0;
    let failed = 0;

    for (const alertId of ids) {
      try {
        await action(alertId);
        succeeded += 1;
      } catch {
        failed += 1;
      }
    }

    await loadAlerts();
    scheduleCountRefresh();
    setBulkActionBusy(false);
    setBulkActionMessage(`${label}: ${succeeded} succeeded, ${failed} failed.`);
  };

  const handleAcknowledgeAllNew = async () => {
    const eligible = alerts
      .filter((a) => {
        const status = String((a as any).status || '').toLowerCase();
        const isClosed = status === 'closed';
        const isAcked = !!(a as any).ackAt || !!(a as any).ackBy || status === 'acked';
        return !isClosed && !isAcked;
      })
      .map((a) => a.alertId)
      .filter((id) => !!id);

    await runBulkAlertAction('Acknowledge All New', eligible, (alertId) => tenantApi.acknowledgeAlert(alertId));
  };

  const handleCloseAllOpen = async () => {
    const eligible = alerts
      .filter((a) => String((a as any).status || '').toLowerCase() !== 'closed')
      .map((a) => a.alertId)
      .filter((id) => !!id);

    if (eligible.length === 0) {
      setBulkActionMessage('No alerts eligible for close all open.');
      return;
    }

    setCloseAllAlertIds([...new Set(eligible)]);
  };

  const confirmCloseAllOpen = async () => {
    if (!closeAllAlertIds?.length || closeAllSubmitting.current) return;
    closeAllSubmitting.current = true;
    try {
      await runBulkAlertAction('Close All Open', closeAllAlertIds,
        (alertId) => tenantApi.closeAlert(alertId, 'Bulk closed by tenant admin'));
    } finally {
      closeAllSubmitting.current = false;
      setCloseAllAlertIds(null);
    }
  };

  if (loading) return <div className="portal-loading">Loading Zurvit...</div>;

  return (
    <div className={`tenant-portal${activeView === 'admin-schedules' ? ' tenant-portal-schedules' : ''}`} onClickCapture={event => { if ((event.target as HTMLElement).closest('nav button, .user-menu-item')) leavePlatform(); }} onClick={() => setShowUserMenu(false)}>
      <ResponsiveNavigation sidebarClassName="portal-nav-sidebar">
        <div className="sidebar-header">
          <div className="logo">Zurvit</div>
          <span className="tenant-name">{tenantName}</span>
        </div>
        
        <div className="sidebar-menu-scroll">
        <nav className="nav-group">
          <div className="nav-label">Main</div>
          <button className={`nav-item ${activeView === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveView('dashboard')}>
            <span className="icon">📊</span> Dashboard
          </button>
          <button className={`nav-item ${activeView === 'alerts' ? 'active' : ''}`} onClick={() => setActiveView('alerts')}>
            <span className="icon">🔔</span> Alerts
          </button>
          <button className={`nav-item ${activeView === 'notifications' ? 'active' : ''}`} onClick={() => setActiveView('notifications')}>
            <span className="icon" aria-hidden="true">&#9993;</span> Notifications
          </button>
          <button className={`nav-item ${activeView === 'schedule' ? 'active' : ''}`} onClick={() => setActiveView('schedule')}>
            <span className="icon">📅</span> On-Call
          </button>
        </nav>

        {isAdmin && (
          <nav className="nav-group">
            <div className="nav-label">Admin</div>
            <button className={`nav-item ${activeView === 'admin-users' ? 'active' : ''}`} onClick={() => setActiveView('admin-users')}>
              <span className="icon">👥</span> Users & Groups
            </button>
            <button className={`nav-item ${activeView === 'admin-roles' ? 'active' : ''}`} onClick={() => setActiveView('admin-roles')}>
              <span className="icon">🛡️</span> Roles & Permissions
            </button>
            <button className={`nav-item ${activeView === 'admin-schedules' ? 'active' : ''}`} onClick={() => setActiveView('admin-schedules')}>
              <span className="icon">📅</span> Schedules
            </button>
            {canManageOverrides && (
              <button className={`nav-item ${activeView === 'admin-oncall-overrides' ? 'active' : ''}`} onClick={() => setActiveView('admin-oncall-overrides')}>
                <span className="icon">🚨</span> On-call Overrides
              </button>
            )}
            <button className={`nav-item ${activeView === 'admin-escalation' ? 'active' : ''}`} onClick={() => setActiveView('admin-escalation')}>
              <span className="icon">📶</span> Escalation
            </button>
            <button className={`nav-item ${activeView === 'admin-channels' ? 'active' : ''}`} onClick={() => setActiveView('admin-channels')}>
              <span className="icon">📢</span> Notification Channels
            </button>
            <button className={`nav-item ${activeView === 'admin-integrations' ? 'active' : ''}`} onClick={() => setActiveView('admin-integrations')}>
              <span className="icon">🔌</span> Integrations
            </button>
            <button className={`nav-item ${activeView === 'admin-workflows' ? 'active' : ''}`} onClick={() => setActiveView('admin-workflows')}>
              <span className="icon">⚡</span> Workflows
            </button>
            <button className={`nav-item ${activeView === 'admin-settings' ? 'active' : ''}`} onClick={() => setActiveView('admin-settings')}>
              <span className="icon">🏢</span> Tenant Settings
            </button>
            <button className={`nav-item ${activeView === 'admin-subscriptions' ? 'active' : ''}`} onClick={() => setActiveView('admin-subscriptions')}>
              <span className="icon">💳</span> Subscriptions
            </button>
            <button className={`nav-item ${activeView === 'admin-sms-gateways' ? 'active' : ''}`} onClick={() => setActiveView('admin-sms-gateways')}>
              <span className="icon">📲</span> SMS Gateways
            </button>
          </nav>
        )}

        {canManageMobileEnrollment && (
          <nav className="nav-group">
            <div className="nav-label">Mobile</div>
            <button className={`nav-item ${activeView === 'admin-mobile-enrollment' ? 'active' : ''}`} onClick={() => setActiveView('admin-mobile-enrollment')}>
              <span className="icon">📱</span> Mobile Enrollment
            </button>
            <button className={`nav-item ${activeView === 'admin-mobile-devices' ? 'active' : ''}`} onClick={() => setActiveView('admin-mobile-devices')}>
              <span className="icon">🔐</span> Mobile Devices
            </button>
          </nav>
        )}

        </div>

        <div className="sidebar-footer">
          <button className={`nav-item ${activeView === 'profile' ? 'active' : ''}`} onClick={() => setActiveView('profile')}>
            <span className="icon">⚙️</span> My Settings
          </button>
        </div>
      </ResponsiveNavigation>

      <main className="portal-main">
        <header className="portal-top-bar">
          <div className="top-bar-actions">
            <HeaderNotifications
              items={headerNotifications}
              onMarkRead={markNotificationReadOptimistic}
              onClear={clearNotificationOptimistic}
              onClearAllVisible={clearVisibleNotificationsOptimistic}
            />
            {user ? (
              <div className="user-profile" onClick={(e) => { e.stopPropagation(); setShowUserMenu(v => !v); }}>
                <div className="avatar">{getInitials(user)}</div>
                <div className="user-info">
                  <span className="user-name">{user.displayName || user.email}</span>
                  <span className="user-role">{isAdmin ? 'Admin' : 'Member'}</span>
                </div>
                <span className="chevron">{showUserMenu ? '▲' : '▼'}</span>
                {showUserMenu && (
                  <div className="user-menu" onClick={(e) => e.stopPropagation()}>
                    <div className="user-menu-header">
                      <div className="avatar avatar-lg">{getInitials(user)}</div>
                      <div>
                        <div className="user-menu-name">{user.displayName || '—'}</div>
                        <div className="user-menu-email">{user.email}</div>
                      </div>
                    </div>
                    <hr className="user-menu-divider" />
                    <button className="user-menu-item" onClick={() => { setActiveView('profile'); setShowUserMenu(false); }}>
                      Profile Settings
                    </button>
                    <button className="user-menu-item" onClick={() => handleThemeChange(theme === 'light' ? 'dark' : 'light')}>
                      {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                    </button>
                    <hr className="user-menu-divider" />
                    <button className="user-menu-item user-menu-signout" onClick={handleLogout}>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="avatar avatar-placeholder">?</div>
            )}
            <PlatformMenu />
          </div>
        </header>
        <PortalBanner
          items={portalBanners}
          audience={isAdmin ? 'tenant-admin' : 'tenant-user'}
          onDismiss={dismissPortalBanner}
        />
        {(isAdmin || isTeamLead) && (
          <NotificationDeliveryIncidentBanner audience={isAdmin ? 'tenant-admin' : 'tenant-user'} />
        )}
        <OnCallOverrideBanner onManage={canManageOverrides ? () => setActiveView('admin-oncall-overrides') : undefined} />
        <section className={`portal-content${activeView === 'notifications' ? ' portal-content-notifications' : ''}${activeView === 'admin-schedules' ? ' portal-content-schedules' : ''}`}>
          <PlatformShellContent portal={isAdmin ? 'tenant-admin' : 'tenant-user'} page={activeView}>{renderContent()}</PlatformShellContent>
        </section>
      </main>
      {closeAllAlertIds && <div className="modal-overlay" onClick={event => {
        event.stopPropagation();
        if (event.target === event.currentTarget && !closeAllSubmitting.current) setCloseAllAlertIds(null);
      }}>
        <section className="modal bulk-close-confirmation" role="alertdialog" aria-modal="true"
          aria-labelledby="bulk-close-title" aria-describedby="bulk-close-description" aria-busy={bulkActionBusy}>
          <div className="bulk-close-confirmation-heading">
            <AlertTriangle size={22} aria-hidden="true" />
            <h3 id="bulk-close-title">Close all open alerts?</h3>
            <button type="button" className="btn btn-secondary btn-icon" aria-label="Dismiss close alerts confirmation" title="Cancel"
              tabIndex={-1} disabled={bulkActionBusy} onClick={() => setCloseAllAlertIds(null)}><X size={18} /></button>
          </div>
          <p id="bulk-close-description">Close {closeAllAlertIds.length} open {closeAllAlertIds.length === 1 ? 'alert' : 'alerts'} in the current list?
            {' '}This will trigger normal close workflows for each alert.</p>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" ref={closeAllCancelButton} disabled={bulkActionBusy}
              onClick={() => setCloseAllAlertIds(null)}>Cancel</button>
            <button type="button" className="btn btn-danger" ref={closeAllConfirmButton} disabled={bulkActionBusy}
              onClick={() => void confirmCloseAllOpen()}>{bulkActionBusy ? 'Closing alerts...' : `Close ${closeAllAlertIds.length} ${closeAllAlertIds.length === 1 ? 'alert' : 'alerts'}`}</button>
          </div>
        </section>
      </div>}
    </div>
  );
};

interface DashboardViewProps {
  isAdmin: boolean;
  canSeeIngestionWidget: boolean;
  openAlertsCount: number;
  openNewCount: number;
  openAckedCount: number;
  userCount: number;
  currentUserId?: string;
  showOnlyMine?: boolean;
  onGoAlerts: () => void;
  onGoSchedule: () => void;
}

const isSmsGatewayConnected = (gateway: { isEnabled?: boolean; status?: string; connectionId?: string | null }) => (
  !!gateway.isEnabled
  && String(gateway.status || '').toLowerCase() === 'online'
  && !!gateway.connectionId
);

const DashboardView: React.FC<DashboardViewProps> = ({
  isAdmin,
  canSeeIngestionWidget,
  openAlertsCount,
  openNewCount,
  openAckedCount,
  userCount,
  currentUserId,
  showOnlyMine,
  onGoAlerts,
  onGoSchedule,
}) => {
  const [smsGatewayAlertMode, setSmsGatewayAlertMode] = useState('TenantAndSuperAdminDashboard');
  const [hasConnectedTenantSmsGateway, setHasConnectedTenantSmsGateway] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    let cancelled = false;
    const loadSmsGatewayStatus = async () => {
      try {
        const [settings, gateways] = await Promise.all([
          tenantApi.getTenantSettings(),
          tenantApi.getTenantSmsGateways(),
        ]);

        if (cancelled) {
          return;
        }

        setSmsGatewayAlertMode(settings.smsGatewayAlertMode || 'TenantAndSuperAdminDashboard');
        setHasConnectedTenantSmsGateway(gateways.some((gateway) => isSmsGatewayConnected(gateway)));
      } catch {
        if (!cancelled) {
          setSmsGatewayAlertMode('TenantAndSuperAdminDashboard');
          setHasConnectedTenantSmsGateway(true);
        }
      }
    };

    void loadSmsGatewayStatus();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const showSmsGatewayAlert = isAdmin
    && smsGatewayAlertMode !== 'Disabled'
    && !hasConnectedTenantSmsGateway;

  return (
    <div className="admin-dashboard-view">
      <div className="dashboard-welcome">
        <h2>Dashboard</h2>
        <p>Overview of alerts, on-call coverage, and tenant health.</p>
      </div>
      {showSmsGatewayAlert && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          No active, connected tenant SMS gateway is available. Outbound SMS messages can remain queued until a tenant SMS gateway reconnects.
        </div>
      )}
      <div className="stats-grid">
        <button className="stat-card stat-card-button" onClick={onGoAlerts}>
          <div className="stat-icon stat-icon-alerts">🔔</div>
          <div className="stat-body">
            <span className="stat-label">Open Alerts</span>
            <span className="stat-value">{openAlertsCount}</span>
            <span className="stat-meta-inline">New {openNewCount} · Acked {openAckedCount}</span>
          </div>
        </button>
        <div className="stat-card">
          <div className="stat-icon stat-icon-users">👥</div>
          <div className="stat-body">
            <span className="stat-label">Team Members</span>
            <span className="stat-value">{userCount}</span>
          </div>
        </div>
        <button className="stat-card stat-card-button" onClick={onGoSchedule}>
          <div className="stat-icon stat-icon-schedules">📅</div>
          <div className="stat-body">
            <span className="stat-label">On-Call Schedule</span>
            <span className="stat-value">View</span>
          </div>
        </button>
      </div>
      <div className="dashboard-widgets-grid">
        <React.Suspense fallback={null}>
          <OnCallPresencePanel showIncidents={canSeeIngestionWidget} />
        </React.Suspense>
        <OnCallWidget
          currentUserId={currentUserId}
          showOnlyMine={showOnlyMine}
          onViewSchedule={onGoSchedule}
        />
        {canSeeIngestionWidget ? (
          <IngestionHealthWidget compact={false} />
        ) : (
          <div className="data-table" style={{ padding: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Ingestion Health</h3>
            <p style={{ marginBottom: 0, color: 'var(--text-secondary)' }}>
              Available to Team Lead and Tenant Admin roles.
            </p>
          </div>
        )}
        {isAdmin && (
          <div className="data-table" style={{ padding: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Admin Controls</h3>
            <p style={{ marginBottom: 0, color: 'var(--text-secondary)' }}>
              Use the Admin section to manage users, schedules, channels, workflows, integrations, and SMS gateways.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
